import { useMemo } from 'react';

import type { ApolloClient } from 'apollo-client';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import { type FeatureId, type ITFGAnalyticsCallbackParams } from '../../common/types';
import { isFeatureDismissedForPeriodInTrial } from '../../common/utils/browser';
import { useIsValidAudience } from '../use-is-valid-audience';

import { COHORTS, type CohortValues } from './constants';

const COHORT = 'cohort';
export const NOT_ENROLLED = 'not-enrolled';

type ExperimentEnrollment = {
	cohort: CohortValues;
	isTreatment: boolean;
	isEnrolled: boolean;
	canRenderTreatment: boolean;
	isLoading: boolean;
};

export const useInTrialFeatureGatesExperimentEnrollment = ({
	experimentName,
	isFeatureGateEnabled,
	featureId,
	aggClient,
	tenantId,
	sendTrackEvent,
	sendOperationalEvent,
	skip = false,
	extraAnalyticsAttributes = {},
}: {
	experimentName: string;
	isFeatureGateEnabled: boolean;
	featureId: FeatureId;
	aggClient: ApolloClient<any>;
	tenantId: string;
	sendTrackEvent: (params: ITFGAnalyticsCallbackParams) => void;
	sendOperationalEvent: (params: ITFGAnalyticsCallbackParams) => void;
	skip?: boolean;
	extraAnalyticsAttributes?: Record<string, any>;
}): ExperimentEnrollment => {
	const {
		isValidAudience,
		data: { hasPaymentDetailsOnFile, canUserPayAndUpgrade, daysLeftInTrial },
		isLoading,
	} = useIsValidAudience({
		aggClient,
		tenantId,
		sendOperationalEvent,
		featureId,
		skip: !isFeatureGateEnabled || skip,
	});

	const { cohort, isTreatment, isEnrolled, canRenderTreatment } = useMemo(() => {
		if (skip || !isFeatureGateEnabled || !isValidAudience) {
			return {
				cohort: COHORTS.NOT_ENROLLED,
				isTreatment: false,
				isEnrolled: false,
				canRenderTreatment: false,
				isLoading: false,
			};
		}

		const cohort = FeatureGates.getExperimentValue(experimentName, COHORT, NOT_ENROLLED, {
			fireExperimentExposure: false,
		}) as CohortValues;

		if (cohort === COHORTS.NOT_ENROLLED) {
			return {
				cohort: COHORTS.NOT_ENROLLED,
				isTreatment: false,
				isEnrolled: false,
				canRenderTreatment: false,
				isLoading: false,
			};
		}

		let canRenderTreatment = false;

		if (cohort === COHORTS.TREATMENT && daysLeftInTrial) {
			canRenderTreatment = !isFeatureDismissedForPeriodInTrial(featureId, daysLeftInTrial);
		}

		// manually fire StatSig exposure event
		FeatureGates.manuallyLogExperimentExposure(experimentName);

		sendTrackEvent({
			type: 'sendTrackEvent',
			action: 'exposed',
			actionSubject: 'experiment',
			actionSubjectId: `${experimentName}-exposed`,
			attributes: {
				cohort,
				hasPaymentDetailsOnFile,
				canUserPayAndUpgrade,
				canRenderTreatment,
				daysLeftInTrial,
				featureId,
				...extraAnalyticsAttributes,
			},
			source: experimentName,
		});

		return {
			cohort,
			isTreatment: cohort === COHORTS.TREATMENT,
			isEnrolled: cohort === COHORTS.TREATMENT || cohort === COHORTS.CONTROL,
			canRenderTreatment,
		};
		// Ignoring createAnalyticsEvent as a dependency since it isn't a primitive value and can cause side effects. It also doesn't change between renders.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		skip,
		isFeatureGateEnabled,
		isValidAudience,
		experimentName,
		hasPaymentDetailsOnFile,
		canUserPayAndUpgrade,
		daysLeftInTrial,
		featureId,
		tenantId,
	]);

	return {
		cohort,
		isTreatment,
		isEnrolled,
		canRenderTreatment,
		isLoading,
	};
};

export const useInTrialFeatureGatesExperimentEnrollmentAB = ({
	featureId,
	tenantId,
	aggClient,
	sendTrackEvent,
	sendOperationalEvent,
	skip = false,
	extraAnalyticsAttributes = {},
}: {
	featureId: FeatureId;
	tenantId: string;
	aggClient: ApolloClient<any>;
	sendTrackEvent: (params: ITFGAnalyticsCallbackParams) => void;
	sendOperationalEvent: (params: ITFGAnalyticsCallbackParams) => void;
	skip?: boolean;
	extraAnalyticsAttributes?: Record<string, any>;
}) => {
	return useInTrialFeatureGatesExperimentEnrollment({
		experimentName: 'in_trial_feature_gates_ab',
		isFeatureGateEnabled: fg('in_trial_feature_gates_ab_fg'),
		featureId,
		aggClient,
		tenantId,
		sendTrackEvent,
		sendOperationalEvent,
		skip,
		extraAnalyticsAttributes,
	});
};
